import { getNameFromFileId } from "./get-name-from-file-id";

// Extracts the name from a file ID, without the file extension
const getBaseNameFromId = (id: string): string => {
  const fullName = getNameFromFileId(id);
  const lastDotIndex = fullName.lastIndexOf(".");
  return lastDotIndex !== -1 ? fullName.slice(0, lastDotIndex) : fullName;
};

// Adds a copy number to the base name, e.g., "file (2)"
const appendCopyNumber = (baseName: string, number: number): string => {
  if (!number) return baseName;
  return `${baseName} (${number})`;
};

// Extracts a number inside parentheses from a filename, e.g., "file (3)" => 3
const extractCopyNumber = (filename: string): number | null => {
  const match = filename.match(/\((\d+)\)/);
  return match ? parseInt(match[1], 10) : null;
};

// Removes the " (n)" pattern from a filename
const removeCopyNumber = (filename: string): string => {
  return filename.replace(/\s\(\d+\)/, "");
};

// Determines the next available copy number for a base name
const getNextCopyNumber = (baseNameWithPossibleNumber: string, fileIds?: string[]): number => {
  const startingNumber = extractCopyNumber(baseNameWithPossibleNumber) ?? 0;
  const baseName = removeCopyNumber(baseNameWithPossibleNumber);

  if (!fileIds) return startingNumber;

  for (let i = startingNumber; i <= fileIds.length; i++) {
    const candidateName = appendCopyNumber(baseName, i);
    const alreadyExists = fileIds.some((id) => getBaseNameFromId(id) === candidateName);
    if (!alreadyExists) return i;
  }

  return fileIds.length + 1;
};

// Reconstructs a new file ID from the old one and a new base name
const buildNewFileId = (originalId: string, newBaseName: string): string => {
  const parts = originalId.split("/");
  const isDirectory = originalId.endsWith("/");
  if (isDirectory) parts.pop();
  const filename = parts.pop() as string;
  const hasExtension = filename.includes(".");
  const extension = !isDirectory && hasExtension ? filename.split(".").at(-1) : null;
  const path = parts.join("/") + (parts.length > 0 ? "/" : "");

  return extension ? `${path}${newBaseName}.${extension}` : `${path}${newBaseName}`;
};

// Main entry point to generate a duplicate file ID
export const getDuplicateFileId = (originalId: string, existingFileIds?: string[]): string => {
  const baseName = getBaseNameFromId(originalId);
  const nextCopyNumber = getNextCopyNumber(baseName, existingFileIds);
  const newName = appendCopyNumber(removeCopyNumber(baseName), nextCopyNumber);
  return buildNewFileId(originalId, newName);
};
